import React from 'react';
import {Link, useParams} from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import AreasSection from '../components/AreasSection';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {FiBuilding, FiCheck, FiPhone, FiCalendar, FiStar, FiUser, FiShield, FiClock, FiTarget} = FiIcons;

const BarsRestaurantsCleaningPage = () => {
  const {area} = useParams();
  const currentArea = area ? formatAreaName(area) : 'Aylesbury';

  const handleLinkClick = () => {
    window.scrollTo({top: 0, behavior: 'smooth'});
  };

  const services = [
    {
      title: "Kitchen Deep Cleaning",
      description: "Comprehensive sanitization of commercial kitchens to meet food safety standards and health regulations.",
      features: [
        "Equipment degreasing",
        "Hood and vent cleaning",
        "Floor deep sanitization",
        "Food prep surface disinfection"
      ]
    },
    {
      title: "Dining Area Maintenance",
      description: "Daily cleaning of customer areas to maintain an inviting atmosphere for guests throughout service hours.",
      features: [
        "Table and chair sanitization",
        "Floor mopping and maintenance",
        "Window and mirror cleaning",
        "Bar surface cleaning"
      ]
    },
    {
      title: "Washroom Facilities",
      description: "Thorough cleaning and sanitization of customer and staff washrooms to the highest hygiene standards.",
      features: [
        "Toilet and sink deep cleaning",
        "Floor sanitization",
        "Mirror and fixture cleaning",
        "Consumable restocking"
      ]
    },
    {
      title: "Bar & Service Areas",
      description: "Specialized cleaning for bars, service counters, and staff areas to maintain operational efficiency.",
      features: [
        "Bar surface sanitization",
        "Glass and bottle cleaning",
        "Cash register area cleaning",
        "Staff area maintenance"
      ]
    }
  ];

  const standards = [
    "HACCP compliant cleaning procedures",
    "Food safety regulation adherence",
    "Specialized hospitality cleaning products",
    "Flexible scheduling around service hours",
    "Emergency cleaning services available",
    "Regular equipment maintenance cleaning"
  ];

  const reviews = [
    {
      name: "Marco Rossi",
      business: "Bella Vista Restaurant",
      rating: 5,
      text: `Diamond Cleaning understands our kitchen requirements perfectly. They work around our service hours in ${currentArea} and maintain the highest food safety standards.`,
      date: "1 week ago"
    },
    {
      name: "Sarah Williams",
      business: "The Crown Public House",
      rating: 5,
      text: `Exceptional service for our pub in ${currentArea}. They clean thoroughly every night after closing and our customers always comment on how clean everything looks.`,
      date: "2 weeks ago"
    },
    {
      name: "David Chen",
      business: "Oriental Garden Restaurant",
      rating: 5,
      text: `Professional team that understands the unique needs of restaurant cleaning. Our kitchen has never been cleaner and we've passed every health inspection with flying colors.`,
      date: "3 weeks ago"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header currentArea={currentArea} />

      {/* Hero Section */}
      <section className="relative min-h-screen overflow-hidden pt-20">
        <div className="absolute inset-0 bg-cover bg-center" 
             style={{backgroundImage: `linear-gradient(rgba(0,0,0,0.6), rgba(0,0,0,0.6)), url('/img/restaurant-cleaning1.webp')`}} />
        
        <div className="relative z-10 flex items-center justify-center min-h-screen px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Bar & Restaurant Cleaning Services in {currentArea}
            </h1>
            
            <p className="text-xl sm:text-2xl text-gray-200 mb-8 leading-relaxed max-w-3xl mx-auto">
              Specialized cleaning for hospitality businesses. HACCP compliant kitchen cleaning, dining area maintenance, and comprehensive sanitization services that meet food safety standards.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/book" 
                className="bg-blue-600 text-white px-8 py-4 rounded-lg hover:bg-blue-700 transition-colors font-semibold text-lg flex items-center justify-center space-x-2"
                onClick={handleLinkClick}
              >
                <SafeIcon icon={FiCalendar} className="w-5 h-5" />
                <span>Get Quote Online</span>
              </Link>
              <a 
                href="tel:07878522378"
                className="bg-green-600 text-white px-8 py-4 rounded-lg hover:bg-green-700 transition-colors font-semibold text-lg flex items-center justify-center space-x-2"
              >
                <SafeIcon icon={FiPhone} className="w-5 h-5" />
                <span>Call: 07878 522378</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* About Restaurant Cleaning */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Professional Restaurant & Bar Cleaning in {currentArea}
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                The hospitality industry demands the highest standards of cleanliness and hygiene. Our specialized restaurant and bar cleaning services ensure your establishment meets all food safety regulations while creating an inviting atmosphere for your customers.
              </p>
              <p className="text-lg text-gray-600 mb-8">
                We understand the unique challenges of hospitality cleaning - from grease-heavy kitchen environments to high-traffic dining areas. Our experienced team works efficiently around your service hours, using industry-approved cleaning products and HACCP compliant procedures.
              </p>

              <div className="space-y-4">
                <h3 className="text-xl font-semibold text-gray-900">Our Hospitality Cleaning Standards</h3>
                {standards.map((standard, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-600 flex-shrink-0 mt-1" />
                    <span className="text-gray-700">{standard}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="rounded-2xl overflow-hidden shadow-2xl h-96 bg-cover bg-center" 
                   style={{backgroundImage: `url('/img/restaurant-cleaning1.webp')`}}>
                <div className="absolute inset-0 bg-blue-600 bg-opacity-20"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Our Restaurant & Bar Cleaning Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive cleaning solutions designed specifically for hospitality businesses
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <div key={index} className="bg-gray-50 rounded-2xl p-8 hover:shadow-lg transition-shadow">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  {service.title}
                </h3>
                <p className="text-gray-600 mb-6">
                  {service.description}
                </p>
                <div className="space-y-3">
                  {service.features.map((feature, featureIndex) => (
                    <div key={featureIndex} className="flex items-center space-x-3">
                      <SafeIcon icon={FiCheck} className="w-4 h-4 text-blue-600" />
                      <span className="text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Food Safety Section */}
      <section className="py-20 bg-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Food Safety & Compliance
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Maintaining the highest standards for your business reputation and customer safety
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl p-8 text-center">
              <SafeIcon icon={FiShield} className="w-16 h-16 text-blue-600 mx-auto mb-6" />
              <h3 className="text-xl font-bold text-gray-900 mb-4">HACCP Compliance</h3>
              <p className="text-gray-600">
                All our cleaning procedures follow HACCP principles to ensure food safety at every stage of your operation.
              </p>
            </div>
            <div className="bg-white rounded-2xl p-8 text-center">
              <SafeIcon icon={FiTarget} className="w-16 h-16 text-green-600 mx-auto mb-6" />
              <h3 className="text-xl font-bold text-gray-900 mb-4">Health Inspections</h3>
              <p className="text-gray-600">
                Our thorough cleaning helps ensure your establishment passes health inspections with confidence.
              </p>
            </div>
            <div className="bg-white rounded-2xl p-8 text-center">
              <SafeIcon icon={FiClock} className="w-16 h-16 text-purple-600 mx-auto mb-6" />
              <h3 className="text-xl font-bold text-gray-900 mb-4">After-Hours Service</h3>
              <p className="text-gray-600">
                We work around your business hours to minimize disruption to your service and operations.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Ready to Maintain the Highest Standards?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
            Professional restaurant and bar cleaning that protects your reputation and ensures customer satisfaction in {currentArea}.
          </p>

          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <div className="bg-white/10 rounded-xl p-6">
              <SafeIcon icon={FiClock} className="w-12 h-12 text-white mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">24/7 Availability</h3>
              <p className="text-blue-100">Emergency cleaning when you need it</p>
            </div>
            <div className="bg-white/10 rounded-xl p-6">
              <SafeIcon icon={FiShield} className="w-12 h-12 text-white mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">Food Safety Certified</h3>
              <p className="text-blue-100">HACCP compliant procedures</p>
            </div>
            <div className="bg-white/10 rounded-xl p-6">
              <SafeIcon icon={FiTarget} className="w-12 h-12 text-white mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">Industry Specialists</h3>
              <p className="text-blue-100">Hospitality cleaning experts</p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/book" 
              className="bg-white text-blue-600 px-8 py-4 rounded-lg hover:bg-gray-100 transition-colors font-semibold text-lg flex items-center justify-center space-x-2"
              onClick={handleLinkClick}
            >
              <SafeIcon icon={FiCalendar} className="w-5 h-5" />
              <span>Get Free Quote</span>
            </Link>
            <a 
              href="tel:07878522378"
              className="bg-green-600 text-white px-8 py-4 rounded-lg hover:bg-green-700 transition-colors font-semibold text-lg flex items-center justify-center space-x-2"
            >
              <SafeIcon icon={FiPhone} className="w-5 h-5" />
              <span>Call: 07878 522378</span>
            </a>
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              What Restaurant & Bar Owners Say
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Real feedback from satisfied hospitality clients in {currentArea}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {reviews.map((review, index) => (
              <div key={index} className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                    <SafeIcon icon={FiUser} className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">{review.name}</h4>
                    <p className="text-sm text-gray-600">{review.business}</p>
                  </div>
                </div>
                <div className="flex items-center mb-4">
                  <div className="flex space-x-1 mr-2">
                    {[...Array(review.rating)].map((_, i) => (
                      <SafeIcon key={i} icon={FiStar} className="w-4 h-4 text-yellow-400 fill-current" />
                    ))}
                  </div>
                  <span className="text-sm text-gray-500">{review.date}</span>
                </div>
                <p className="text-gray-700 leading-relaxed">"{review.text}"</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Areas Section */}
      <AreasSection currentArea={currentArea} />
      <Footer />
    </div>
  );
};

const formatAreaName = (area) => {
  return area
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export default BarsRestaurantsCleaningPage;